import type { IOptionsProps, IUseFilterProps } from "@fluidattacks/design";
import { useFilters } from "@fluidattacks/design";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IGroupData } from "pages/organization/groups/types";
import { useFiltersStorage } from "storage/filters";

interface IGroupFilters {
  dataset: IGroupData[];
  organizationName: string;
  setFilteredDataset: React.Dispatch<React.SetStateAction<IGroupData[]>>;
}

const useGroupsFilters = ({
  dataset,
  organizationName,
  setFilteredDataset,
}: Readonly<IGroupFilters>): IUseFilterProps<IGroupData> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();
  const options: IOptionsProps<IGroupData>[] = useMemo(
    (): IOptionsProps<IGroupData>[] => [
      {
        filterOptions: [
          {
            filterFn: "includesInsensitive",
            key: "name",
            label: t("organization.tabs.groups.newGroup.name.text"),
            type: "text",
          },
        ],
        label: t("organization.tabs.groups.newGroup.name.text"),
      },
      {
        filterOptions: [
          {
            key: "plan",
            label: t("organization.tabs.groups.plan"),
            options: [
              { label: "Advanced", value: "Advanced" },
              { label: "Essential", value: "Essential" },
              { label: "Oneshot", value: "Oneshot" },
            ],
            type: "checkboxes",
          },
        ],
        label: t("organization.tabs.groups.plan"),
      },
    ],
    [t],
  );

  const handleGetValue = useCallback((): string | null => {
    return getValues("groups", organizationName) as string | null;
  }, [getValues, organizationName]);
  const handleSetValue = useCallback(
    (value: string): void => {
      void setValues("groups", value, organizationName);
    },
    [setValues, organizationName],
  );

  return useFilters({
    dataset,
    getValues: handleGetValue,
    options,
    setFilteredDataset,
    setValues: handleSetValue,
  });
};

export { useGroupsFilters };
